import React, { memo } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import styles from "./add-request-technical-support-drawer-modal.module.scss";
import { Title } from "@src/components/atoms/title/title";
import { DrawerIconContainer } from "@src/components/atoms/drawer-icon-container/drawer-icon-container";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import { useAddRequestTechnicalSupportDrawerModal } from "./use-add-request-technical-support-drawer-modal-hook";
import { MultipleDropAreaAddImage } from "@src/components/atoms/multiple-drop-area-add-image/multiple-drop-area-add-image";
import { useI18N } from "@src/language/language-context";
import AddRequestTechnicalSupport from "@src/assets/svg/add-request-technical-support-drawer-modal-icon.svg";
import SupportIcon from "@src/assets/svg/support-icon.svg";
import ArrowIcon from "@src/assets/svg/arrow.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { MAX_TICKET_TITLE_LENGTH } from "@src/cloud-connect/support/constants/constants";

const cssClassName = "add-request-technical-support-drawer-modal";

export const AddRequestTechnicalSupportDrawerModal = memo(() => {
  const {
    machineName,
    issueTitle,
    issueDetails,
    onChangeIssueTitle,
    onChangeIssueDetails,
    handleSubmitRequestTechnicalSupport,
    isSubmitDisabled,
    isSubmitRequestTechnicalSupportLoading,
    isSSEUnavailable,
    isDetailsExpanded,
    handleToggleDetails,
    images,
    addImages,
    removeImage,
  } = useAddRequestTechnicalSupportDrawerModal();

  const i18n = useI18N();

  const texts = {
    title: i18n(`modules.addRequestTechnicalSupportDrawerModal.title`),
    supportDetails: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.supportDetails",
    ),
    machine: i18n("modules.addRequestTechnicalSupportDrawerModal.machine"),
    issueTitle: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueTitle",
    ),
    issueTitlePlaceholder: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueTitlePlaceholder",
    ),
    issueDetails: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueDetails",
    ),
    issueDetailsPlaceholder: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueDetailsPlaceholder",
    ),
    requestButton: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.requestButton",
    ),
    sseUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
    imageRemoveAlt: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.imageRemoveAlt",
    ),
    imageTooMany: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.imageTooMany",
    ),
    imageSizeTooLarge: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.imageSizeTooLarge",
    ),
    imageInvalidType: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.imageInvalidType",
    ),
  };

  return (
    <DrawerModal id="addRequestTechnicalSupportDrawerModal">
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={24}>
          <Title tag="h2" text={texts.title} />
          <DrawerIconContainer icon={AddRequestTechnicalSupport} />

          <div className={styles[`${cssClassName}__content-wrapper`]}>
            <FlexRowContainer horizontalGap={6}>
              <img
                className={styles[`${cssClassName}__container-icon`]}
                alt={texts.supportDetails}
                src={SupportIcon}
              />
              <p className={styles[`${cssClassName}__subtitle`]}>
                {texts.supportDetails}
              </p>
              <IconButton
                icon={ArrowIcon}
                fun={handleToggleDetails}
                rotate={isDetailsExpanded}
              />
            </FlexRowContainer>

            {isDetailsExpanded && (
              <div className={styles[`${cssClassName}__details-box`]}>
                <div className={styles[`${cssClassName}__session-section`]}>
                  <div className={styles[`${cssClassName}__session-label`]}>
                    {texts.machine}
                  </div>
                  <span className={styles[`${cssClassName}__detail-value`]}>
                    {machineName}
                  </span>
                </div>

                <div className={styles[`${cssClassName}__session-section`]}>
                  <label className={styles[`${cssClassName}__session-label`]}>
                    {texts.issueTitle}
                  </label>
                  <input
                    id="issueTitle"
                    type="text"
                    value={issueTitle}
                    onChange={(e) => onChangeIssueTitle(e.target.value)}
                    placeholder={texts.issueTitlePlaceholder}
                    className={styles[`${cssClassName}__input-and-textarea`]}
                    maxLength={MAX_TICKET_TITLE_LENGTH}
                  />
                </div>

                <div className={styles[`${cssClassName}__session-section`]}>
                  <label className={styles[`${cssClassName}__session-label`]}>
                    {texts.issueDetails}
                  </label>
                  <textarea
                    id="issueDetails"
                    value={issueDetails}
                    onChange={(e) => onChangeIssueDetails(e.target.value)}
                    placeholder={texts.issueDetailsPlaceholder}
                    className={styles[`${cssClassName}__input-and-textarea`]}
                    maxLength={MAX_TICKET_TITLE_LENGTH}
                  />
                </div>

                <MultipleDropAreaAddImage
                  images={images}
                  onAdd={addImages}
                  onRemove={removeImage}
                  disabled={isSubmitRequestTechnicalSupportLoading}
                  removeLabel={texts.imageRemoveAlt}
                  errorMessages={{
                    tooMany: texts.imageTooMany,
                    tooLarge: texts.imageSizeTooLarge,
                    invalid: texts.imageInvalidType,
                  }}
                />
                <div className={styles[`${cssClassName}__button-wrapper`]}>
                  <CustomTooltip
                    className={styles[`${cssClassName}__submit-tooltip`]}
                    content={
                      isSSEUnavailable ? texts.sseUnavailableTooltip : ""
                    }
                    position="top"
                  >
                    <ButtonWithLoader
                      fun={handleSubmitRequestTechnicalSupport}
                      text={texts.requestButton}
                      disabled={isSubmitDisabled}
                      loading={isSubmitRequestTechnicalSupportLoading}
                      variant={["primary", "full-width"]}
                    />
                  </CustomTooltip>
                </div>
              </div>
            )}
          </div>
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
});

AddRequestTechnicalSupportDrawerModal.displayName =
  "AddRequestTechnicalSupportDrawerModal";
